import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Box,
  CircularProgress,
  Alert,
  Card,
  CardMedia,
} from "@mui/material";
import { ArrowBack, Save, CloudUpload } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { API_ENDPOINT } from "../../constants/constants";
import { getHeader, getMultipartHeader, getToken } from "../../utils/util";

const BannerForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState({
    title: "",
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [existingImageUrl, setExistingImageUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (isEditMode) {
      fetchBanner();
    }
  }, [id]);

  const fetchBanner = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_ENDPOINT}admin/banners/${id}`, {
        ...getHeader(),
      });

      if (!response.ok) throw new Error("Failed to fetch banner");

      const data = await response.json();
      setFormData({
        title: data.title,
      });
      setExistingImageUrl(data.imageUrl);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      setError("Invalid file type. Only JPG, PNG and WebP are allowed.");
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError("File size should be less than 5MB");
      return;
    }

    setImageFile(file);
    setError(null);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      setError("Title is required");
      return;
    }

    if (!isEditMode && !imageFile) {
      setError("Image is required for new banners");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const formDataToSend = new FormData();
      formDataToSend.append("title", formData.title.trim());

      if (imageFile) {
        formDataToSend.append("image", imageFile);
      }

      const url = isEditMode ? `admin/banners/${id}` : "admin/banners";
      const method = isEditMode ? "PUT" : "POST";
      const response = await fetch(`${API_ENDPOINT}${url}`, {
        method,
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
        body: formDataToSend,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to save banner");
      }

      setSuccess(true);
      setTimeout(() => {
        navigate(-1);
      }, 1500);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (loading && isEditMode && !formData.title) {
    return (
      <Container sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  const displayImage = imagePreview || existingImageUrl;

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Button startIcon={<ArrowBack />} onClick={handleBack} sx={{ mb: 2 }}>
        Back to Banners
      </Button>

      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" component="h1" gutterBottom>
          {isEditMode ? "Edit Banner" : "Add New Banner"}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Banner {isEditMode ? "updated" : "created"} successfully!
            Redirecting...
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <TextField
            fullWidth
            label="Banner Title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            margin="normal"
            disabled={loading}
          />

          <Box sx={{ mt: 3, mb: 2 }}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUpload />}
              disabled={loading}
              fullWidth
            >
              {imageFile
                ? "Change Image"
                : isEditMode
                ? "Upload New Image (Optional)"
                : "Upload Image *"}
              <input
                type="file"
                hidden
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handleImageChange}
              />
            </Button>
            <Typography
              variant="caption"
              display="block"
              sx={{ mt: 1, color: "text.secondary" }}
            >
              Allowed formats: JPG, PNG, WebP (Max 5MB)
            </Typography>
          </Box>

          {imageFile && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Selected: {imageFile.name} (
              {(imageFile.size / 1024 / 1024).toFixed(2)} MB)
            </Alert>
          )}

          {displayImage && (
            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {imagePreview ? "New Image Preview:" : "Current Image:"}
              </Typography>
              <Card>
                <CardMedia
                  component="img"
                  image={displayImage}
                  alt={formData.title || "Banner preview"}
                  sx={{
                    width: "100%",
                    height: 250,
                    objectFit: "cover",
                  }}
                />
              </Card>
            </Box>
          )}

          <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              startIcon={<Save />}
              disabled={loading}
              fullWidth
            >
              {loading ? (
                <CircularProgress size={24} />
              ) : isEditMode ? (
                "Update Banner"
              ) : (
                "Create Banner"
              )}
            </Button>
            <Button variant="outlined" onClick={handleBack} disabled={loading}>
              Cancel
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default BannerForm;
